﻿import { isDefined } from '@sgme/fp';
import { HStack, VStack } from '@sgme/ui';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import clsx from 'clsx';
import deepEqual from 'fast-deep-equal';
import { FormattedMessage, FormattedNumber } from 'react-intl';

import { CURRENCY_PAIR_PRECISION_OFFSET, RATES_CONTAINER_HEIGHT } from '@/App/tiles/CashTile/shared/constants.ts';
import { RatePanel } from '@/App/tiles/CashTile/shared/RatePanel.tsx';
import { isEspStatus, isRfsStatus } from '@/App/tiles/CashTile/shared/utils.ts';
import { SpotRates } from '@/App/tiles/CashTile/SpotTile/SpotRates.tsx';
import { ErrorRates } from '@/App/tiles/shared/ErrorRates.tsx';
import { ForbiddenExecutionTooltip } from '@/App/tiles/shared/ForbiddenExecutionTooltip.tsx';
import { SaleTier } from '@/App/tiles/shared/SaleTier.tsx';
import { FormattedPrice, getHighlightDigitPosition } from '@/components/ui/FormattedPrice.tsx';
import { checkIfUserCanTrade, checkIfUserInternalSales, useUserContext } from '@/context/UserContext.tsx';
import { useExecuteCashRfsMutation } from '@/store/cash/api/index.ts';
import { selectCashPriceStatus } from '@/store/cash/selectors/prices.ts';
import { selectCashTile } from '@/store/cash/selectors/tile.ts';
import { useAppSelector } from '@/store/hooks.ts';
import { getSignalRConnectionId } from '@/store/streaming/slice';
import { selectTileClientId } from '@/store/tiles/selectors.ts';
import { getUserPreferences } from '@/store/userPreferences/slice.ts';
import { getRateComponentStyling } from '@/utils/rateComponentStyling.ts';

import '../shared/RatePanel.css';

export type ForwardRatesProps = {
  tileId: string;
};

export const ForwardRates = (props: ForwardRatesProps) => {
  const { tileId } = props;

  const { sgwtWebAnalytics } = useSgwtWidgets();

  const userPreferences = useAppSelector((state) => state.userPreferences);
  const preferedEmails = userPreferences.splitNotifications
    ? userPreferences.splitNotificationsEmailsCash
    : userPreferences.emails;

  // 💡 Getting user type and permissions
  const userContextValue = useUserContext();
  const notificationEmail = preferedEmails.length > 0 ? preferedEmails.join(';') : userContextValue.currentUser.login;

  const {
    tile,
    currency1,
    currency2,
    notional,
    notionalCurrencyIndex,
    maturityDate,
    clientId,
    rfsId,
    priceStatus,
    signalRConnectionId,
    quote,
    rateError,
  } = useAppSelector((state) => {
    const tile = selectCashTile(state, tileId);
    const rfsId = tile?.properties.rfs?.rfsId;
    const priceStatus = selectCashPriceStatus(state, tileId);
    const signalRConnectionId = getSignalRConnectionId(state);
    const clientId = selectTileClientId(state, tileId);

    return {
      tile,
      currency1: tile?.properties.currency1,
      currency2: tile?.properties.currency2,
      notional: tile?.properties.notional,
      notionalCurrencyIndex: tile?.properties.notionalCurrencyIndex,
      maturityDate: tile?.properties.maturityDate,
      clientId,
      rfsId,
      priceStatus,
      signalRConnectionId,
      quote: tile?.properties?.rfs?.status === 'STREAMED' ? tile?.properties.rfs?.quote : undefined,
      rateError: tile?.properties.error,
    };
  }, deepEqual);

  const canTrade = checkIfUserCanTrade(userContextValue);
  const isSaleUser = checkIfUserInternalSales(userContextValue);

  const [executeCashRfs] = useExecuteCashRfsMutation({ fixedCacheKey: rfsId });

  /**
   * Check if all required properties are defined for RFS execution
   */
  const hasAllRequiredProperties = () => {
    return (
      isDefined(tile) &&
      isDefined(currency1) &&
      isDefined(currency2) &&
      isDefined(notional) &&
      isDefined(notionalCurrencyIndex) &&
      isDefined(maturityDate) &&
      isDefined(clientId) &&
      isDefined(quote)
    );
  };

  /**
   * Check if required data is available to show rates
   */
  const hasRequiredData = () => {
    return (
      isDefined(notificationEmail) &&
      isDefined(rfsId) &&
      isDefined(signalRConnectionId) &&
      isDefined(priceStatus) &&
      isRfsStatus(priceStatus.status)
    );
  };

  if (isDefined(rateError)) {
    return <ErrorRates code={rateError.code} message={rateError.message} multipass={rateError.multipass} />;
  }

  if (isEspStatus(priceStatus.status)) {
    return <SpotRates tileId={tileId} />;
  }

  if (!hasRequiredData()) {
    return <EmptyRatesComponent canTrade={canTrade} />;
  }

  if (priceStatus.status === 'rfs-required' || priceStatus.status === 'rfs-pending') {
    return <AwaitingRatesComponent canTrade={canTrade} />;
  }

  // Type guard: at this point, priceStatus should be CashRfsPrice
  if (priceStatus.status !== 'rfs-price') {
    return <EmptyRatesComponent canTrade={canTrade} />;
  }

  const { currencyPairPrecision, rates, ratesWithoutMargin, swapPoints, spread } = priceStatus;

  const highlightDigitPosition = getHighlightDigitPosition(priceStatus.currencyPairPrecision, {
    bid: priceStatus.commercialMarginSpotPoints.bid + priceStatus.commercialMarginSwapPoints.bid,
    ask: priceStatus.commercialMarginSpotPoints.ask + priceStatus.commercialMarginSwapPoints.ask,
  });

  /**
   * Execute RFS for bid or ask
   * in FX: fx-web/app/src/epics/metaSelectors/cash/cashRfsExecuteRequest.ts
   */
  const executeRfs = (way: 'Bid' | 'Ask') => {
    if (!priceStatus.isExecutable || !canTrade || priceStatus.isReadonly) {
      console.error('Rates are not executable');
      return;
    }

    if (!hasAllRequiredProperties()) {
      console.error('all required properties are not defined');
      return;
    }

    if (
      !tile ||
      !rfsId ||
      !clientId ||
      !signalRConnectionId ||
      !currency1 ||
      !currency2 ||
      !notional ||
      !notionalCurrencyIndex ||
      !maturityDate ||
      !quote
    ) {
      console.error('all required properties are not defined');
      return;
    }

    const isBid = way === 'Bid';
    const eventLabel = isBid ? 'bid' : 'ask';
    const spotRate = isBid ? priceStatus.spotRates.bid : priceStatus.spotRates.ask;
    const priceWithoutMargin = isBid ? ratesWithoutMargin.bid : ratesWithoutMargin.ask;
    const priceWithMargin = isBid ? rates.bid : rates.ask;
    const forwardPoints = isBid ? swapPoints.bid : swapPoints.ask;

    // FORWARD
    if (tile.type === 'FxFwd' || tile.type === 'FxSpot') {
      sgwtWebAnalytics?.trackEvent('Cash', 'RFS executed', eventLabel);

      executeCashRfs({
        productName: 'FxFwd',
        tileId,
        quoteId: priceStatus.quoteId,
        rfsId,
        clientId,
        signalRConnectionId,
        currency1,
        currency2,
        notional,
        notionalCurrencyIndex,
        maturityDate,
        spotRate,
        way,
        priceWithoutMargin,
        priceWithMargin,
        spotWithoutMargin: priceStatus.execution.spotWithoutMargin,
        spotWithMargin: priceStatus.execution.spotWithMargin,
        forwardPoints,
        executionClickByFrontTimestamp: Date.now(),
        lastQuoteFromBackTimestamp: priceStatus.backTimestamp,
        lastQuoteReceivedByFrontTimestamp: priceStatus.frontTimestamp,
        userLocalDateTime: Date.now(),
        notificationEmail,
      });

      return;
    }

    // NDF
    if (tile.type === 'FxNdf' && tile.properties.fixingSource && tile.properties.fixingDate) {
      sgwtWebAnalytics?.trackEvent('Cash', 'RFS executed', eventLabel);

      executeCashRfs({
        productName: 'FxNdf',
        tileId,
        quoteId: priceStatus.quoteId,
        rfsId,
        clientId,
        signalRConnectionId,
        currency1,
        currency2,
        notional,
        notionalCurrencyIndex,
        maturityDate,
        spotRate,
        way,
        priceWithoutMargin,
        priceWithMargin,
        spotWithoutMargin: priceStatus.execution.spotWithoutMargin,
        spotWithMargin: priceStatus.execution.spotWithMargin,
        forwardPoints,
        executionClickByFrontTimestamp: Date.now(),
        lastQuoteFromBackTimestamp: priceStatus.backTimestamp,
        lastQuoteReceivedByFrontTimestamp: priceStatus.frontTimestamp,
        userLocalDateTime: Date.now(),
        notificationEmail,
        reference: tile.properties.fixingSource,
        fixingDate: tile.properties.fixingDate,
      });
    }
  };

  const executeBid = () => executeRfs('Bid');
  const executeAsk = () => executeRfs('Ask');

  if (tile?.type === 'FxSpot') {
    return (
      <HStack fullWidth className="position-relative" style={{ height: RATES_CONTAINER_HEIGHT }} data-e2e="rates">
        <RatePanel
          type="spot"
          side="left"
          way="Bid"
          rate={rates.bid}
          rateType={priceStatus.isExecutable ? 'executable' : 'indicative'}
          highlightPosition={highlightDigitPosition.bid ?? currencyPairPrecision + 1}
          currencyPairPrecision={currencyPairPrecision}
          currency1={currency1 ?? ''}
          currency2={currency2 ?? ''}
          notionalCurrencyIndex={notionalCurrencyIndex ?? 1}
          isSaleUser={isSaleUser}
          isExecutable={priceStatus.isExecutable}
          canTrade={canTrade}
          isReadonly={priceStatus.isReadonly}
          onExecute={executeBid}
        />

        <RatePanel
          type="spot"
          side="right"
          way="Ask"
          rate={rates.ask}
          rateType={priceStatus.isExecutable ? 'executable' : 'indicative'}
          highlightPosition={highlightDigitPosition.ask ?? currencyPairPrecision + 1}
          currencyPairPrecision={currencyPairPrecision}
          currency1={currency1 ?? ''}
          currency2={currency2 ?? ''}
          notionalCurrencyIndex={notionalCurrencyIndex ?? 1}
          isSaleUser={isSaleUser}
          isExecutable={priceStatus.isExecutable}
          canTrade={canTrade}
          isReadonly={priceStatus.isReadonly}
          onExecute={executeAsk}
        />

        <HStack horizontalAlignment="center" className="position-absolute bottom-0 start-0 end-0" data-e2e="spread">
          <div className="px-1 text-black bg-light">{spread}</div>
        </HStack>

        {priceStatus.isSaleTier && <SaleTier />}
      </HStack>
    );
  }

  return (
    <HStack fullWidth className="position-relative" style={{ height: RATES_CONTAINER_HEIGHT }} data-e2e="rates">
      <ForwardRatePanel
        side="left"
        way="Bid"
        rate={rates.bid}
        spotRate={priceStatus.spotRates.bid}
        swapPoints={swapPoints.bid}
        highlightPosition={highlightDigitPosition.bid ?? currencyPairPrecision + 1}
        currencyPairPrecision={currencyPairPrecision}
        currency1={currency1 ?? ''}
        currency2={currency2 ?? ''}
        notionalCurrencyIndex={notionalCurrencyIndex ?? 1}
        isSaleUser={isSaleUser}
        isExecutable={priceStatus.isExecutable}
        canTrade={canTrade}
        isReadonly={priceStatus.isReadonly}
        onExecute={executeBid}
      />

      <ForwardRatePanel
        side="right"
        way="Ask"
        rate={rates.ask}
        spotRate={priceStatus.spotRates.ask}
        swapPoints={swapPoints.ask}
        highlightPosition={highlightDigitPosition.ask ?? currencyPairPrecision + 1}
        currencyPairPrecision={currencyPairPrecision}
        currency1={currency1 ?? ''}
        currency2={currency2 ?? ''}
        notionalCurrencyIndex={notionalCurrencyIndex ?? 1}
        isSaleUser={isSaleUser}
        isExecutable={priceStatus.isExecutable}
        canTrade={canTrade}
        isReadonly={priceStatus.isReadonly}
        onExecute={executeAsk}
      />

      <HStack horizontalAlignment="center" className="position-absolute bottom-0 start-0 end-0" data-e2e="spread">
        <div className="px-1 text-black bg-light">{spread}</div>
      </HStack>

      {priceStatus.isSaleTier && <SaleTier />}
    </HStack>
  );
};

/**
 * Component displayed when no rates are available (re-price required)
 */
const EmptyRatesComponent = ({ canTrade }: { canTrade: boolean }) => (
  <HStack
    fullWidth
    className="position-relative border-top border-bottom"
    style={{ height: RATES_CONTAINER_HEIGHT }}
    data-e2e="empty-rates"
  >
    <div className="w-50 h-100 border-end text-center fw-semibold" style={{ height: RATES_CONTAINER_HEIGHT }}>
      <VStack
        horizontalAlignment="center"
        verticalAlignment="center"
        fullWidth
        fullHeight
        className={clsx('px-2 py-1', ...getRateComponentStyling({ executable: false, cantTrade: !canTrade }))}
      >
        <div className="text-secondary">
          <FormattedMessage id="CashTile.ForwardRates.repriceForBid" values={{ br: <br /> }} />
        </div>
      </VStack>
    </div>

    <div className="w-50 h-100 text-center fw-semibold" style={{ height: RATES_CONTAINER_HEIGHT }}>
      <VStack
        horizontalAlignment="center"
        verticalAlignment="center"
        fullWidth
        fullHeight
        className={clsx('px-2 py-1', ...getRateComponentStyling({ executable: false, cantTrade: !canTrade }))}
      >
        <div className="text-secondary">
          <FormattedMessage id="CashTile.ForwardRates.repriceForAsk" values={{ br: <br /> }} />
        </div>
      </VStack>
    </div>
  </HStack>
);

/**
 * Component displayed when rates are being requested (awaiting price)
 */
const AwaitingRatesComponent = ({ canTrade }: { canTrade: boolean }) => (
  <HStack
    fullWidth
    className="position-relative border-top border-bottom"
    style={{ height: RATES_CONTAINER_HEIGHT }}
    data-e2e="empty-rates"
  >
    <div className="w-50 h-100 border-end text-center fw-semibold" style={{ height: RATES_CONTAINER_HEIGHT }}>
      <VStack
        horizontalAlignment="center"
        verticalAlignment="center"
        fullWidth
        fullHeight
        className={clsx('px-2 py-1', ...getRateComponentStyling({ executable: false, cantTrade: !canTrade }))}
      >
        <div className="text-secondary">
          <FormattedMessage id="CashTile.ForwardRates.awaitingPrice" values={{ br: <br /> }} />
        </div>
      </VStack>
    </div>

    <div className="w-50 h-100 text-center fw-semibold" style={{ height: RATES_CONTAINER_HEIGHT }}>
      <VStack
        horizontalAlignment="center"
        verticalAlignment="center"
        fullWidth
        fullHeight
        className={clsx('px-2 py-1', ...getRateComponentStyling({ executable: false, cantTrade: !canTrade }))}
      >
        <div className="text-secondary">
          <FormattedMessage id="CashTile.ForwardRates.awaitingPrice" values={{ br: <br /> }} />
        </div>
      </VStack>
    </div>
  </HStack>
);

type ForwardRatePanelProps = {
  side: 'left' | 'right';
  way: 'Bid' | 'Ask';
  rate: number;
  spotRate: number;
  swapPoints: number;
  highlightPosition: number;
  currencyPairPrecision: number;
  currency1: string;
  currency2: string;
  notionalCurrencyIndex: number;
  isSaleUser: boolean;
  isExecutable: boolean;
  canTrade: boolean;
  isReadonly: boolean;
  onExecute: () => void;
};

/**
 * Component displaying a single rate panel (bid or ask)
 */
const ForwardRatePanel = ({
  side,
  way,
  rate,
  spotRate,
  swapPoints,
  highlightPosition,
  currencyPairPrecision,
  currency1,
  currency2,
  notionalCurrencyIndex,
  isSaleUser,
  isExecutable,
  canTrade,
  isReadonly,
  onExecute,
}: ForwardRatePanelProps) => {
  const alignment = side === 'left' ? 'start' : 'end';
  const isBid = way === 'Bid';

  const isSell = (isBid && notionalCurrencyIndex === 1) || (!isBid && notionalCurrencyIndex === 2);
  const { showColoredPrices } = useAppSelector(getUserPreferences);

  const messageId = isSaleUser
    ? isSell
      ? 'CashTile.Rates.clientSells'
      : 'CashTile.Rates.clientBuys'
    : isSell
      ? 'CashTile.Rates.youSell'
      : 'CashTile.Rates.youBuy';

  const currency = notionalCurrencyIndex === 1 ? currency1 : currency2;

  const detailsAlignment = side === 'left' ? 'start' : 'end';

  return (
    <div className={`w-50 h-100 ${side === 'left' ? 'border-end' : ''} text-center fw-semibold`}>
      <ForbiddenExecutionTooltip placement={side}>
        <VStack
          horizontalAlignment={alignment}
          verticalAlignment="full"
          fullWidth
          fullHeight
          className={clsx('px-2 py-1', 'rate-panel', {
            executable: isExecutable,
            cantTrade: isReadonly || !canTrade,
            sell: isSell,
            buy: !isSell,
            'colored-prices': showColoredPrices,
          })}
          onDoubleClick={onExecute}
        >
          <div className="fs-12 line-height-1" data-e2e={`${side}-rate-way`}>
            <FormattedMessage id={messageId} values={{ currency }} />
          </div>

          <HStack verticalAlignment="end" fullWidth flex="expand">
            <FormattedPrice
              value={rate}
              exponentCount={2}
              highlightDigitPosition={highlightPosition}
              precision={currencyPairPrecision + CURRENCY_PAIR_PRECISION_OFFSET}
              size="lg"
              data-e2e={`${side}-rate`}
              data-e2e-rate-type={isExecutable ? 'executable' : 'indicative'}
              data-e2e-can-trade={canTrade}
              plusPrefixHidden
              showColoredPrices={showColoredPrices}
            />
          </HStack>

          <div
            className={`fs-12 text-${detailsAlignment} ${side === 'right' ? 'align-self-end' : ''}`}
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, auto)',
              gap: '0 0.5rem',
              textAlign: side === 'right' ? 'right' : 'left',
              fontSize: '10px',
              lineHeight: '15px',
            }}
          >
            {side === 'left' ? (
              <>
                <div className="text-end">Spot</div>
                <div className="text-start" data-e2e={`${side}-spot-rate`}>
                  <FormattedNumber
                    value={spotRate}
                    maximumSignificantDigits={currencyPairPrecision + CURRENCY_PAIR_PRECISION_OFFSET}
                  />
                </div>

                <div className="text-end">Pts</div>
                <div className="text-start" data-e2e={`${side}-swap-points`}>
                  <FormattedNumber
                    value={swapPoints}
                    maximumSignificantDigits={currencyPairPrecision + CURRENCY_PAIR_PRECISION_OFFSET}
                    signDisplay="always"
                  />
                </div>
              </>
            ) : (
              <>
                <div className="text-end" data-e2e={`${side}-spot-rate`}>
                  <FormattedNumber
                    value={spotRate}
                    maximumSignificantDigits={currencyPairPrecision + CURRENCY_PAIR_PRECISION_OFFSET}
                  />
                </div>
                <div className="text-start">Spot</div>

                <div className="text-end" data-e2e={`${side}-swap-points`}>
                  <FormattedNumber
                    value={swapPoints}
                    maximumSignificantDigits={currencyPairPrecision + CURRENCY_PAIR_PRECISION_OFFSET}
                    signDisplay="always"
                  />
                </div>
                <div className="text-start">Pts</div>
              </>
            )}
          </div>
        </VStack>
      </ForbiddenExecutionTooltip>
    </div>
  );
};
